import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from 'GlobalContext';
import { useModal } from 'components/modals/ModalContext';
import { useSettings } from 'services/settings/SettingsContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useAllowedRight } from 'services/permissions/permissionChecks';
import WarningModal from 'components/modals/WarningModal';
import Dropdown from 'components/forms/basefields/Dropdown';
import DateInput from 'components/forms/basefields/DateInput';
import TextInput from 'components/forms/basefields/TextInput';
import MultiSelect from 'components/forms/basefields/MultiSelect';
import PrimaryButton from 'components/buttons/PrimaryButton';
import SecondaryButton from 'components/buttons/SecondaryButton';
import { saveData } from 'services/api/saveData';
import { formatAmount } from 'services/utils/amountFormatting';
import { parseDurationFromAmount } from 'services/utils/parseDurationFromAmount';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faLock, faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import { FormType } from '../../views/forms/FormTypes';
import ContractScheduleTaskRows from './ContractScheduleTaskRows';
import ContractScheduleDefaultTaskRows from './ContractScheduleDefaultTaskRows';
import { ContractJobScheduleType, ContractJobScheduleTaskType, ContractType, ContractWorkflowOptionType } from './ContractTypes';
import { JobWorkflowDefaultTaskType } from '../settings/jobs/JobTypeDefaultsTypes';
import '../../style/scss/card.scss';
import '../../style/scss/custom-fields.scss';

// Set the interval options of the repetition dropdown
const INTERVAL_OPTIONS = [
    { value: 'weekly', name: 'contract.interval.weekly' },
    { value: 'monthly', name: 'contract.interval.monthly' },
    { value: 'quarterly', name: 'contract.interval.quarterly' },
    { value: 'half_yearly', name: 'contract.interval.half_yearly' },
    { value: 'yearly', name: 'contract.interval.yearly' },
    { value: 'custom', name: 'contract.interval.custom' },
    { value: 'week_numbers', name: 'contract.interval.week_numbers' },
];

// Set the custom unit options of the repetition dropdown
const CUSTOM_UNIT_OPTIONS = [
    { value: 'day', name: 'contract.custom_unit.day' },
    { value: 'week', name: 'contract.custom_unit.week' },
    { value: 'month', name: 'contract.custom_unit.month' },
    { value: 'year', name: 'contract.custom_unit.year' },
];

// Set the generation mode options
const GENERATION_MODE_OPTIONS = [
    { value: 'lead_time', name: 'contract.generation_mode.lead_time' },
    { value: 'yearly_upfront', name: 'contract.generation_mode.yearly_upfront' },
    { value: 'after_previous', name: 'contract.generation_mode.after_previous' },
];

// Set the lead time presets
const LEAD_TIME_OPTIONS = [
    { value: '7', name: 'contract.lead_time.week' },
    { value: '14', name: 'contract.lead_time.two_weeks' },
    { value: '30', name: 'contract.lead_time.month' },
    { value: '60', name: 'contract.lead_time.two_months' },
    { value: '90', name: 'contract.lead_time.three_months' },
];

// Set the week number options
const WEEK_NUMBER_OPTIONS = Array.from({ length: 53 }, (_, index) => ({
    value: String(index + 1),
    name: String(index + 1),
}));

// Renders one label value row of the schedule card
export const ScheduleRow: React.FC<{
    label: string;
    tooltipText?: string;
    children: React.ReactNode;
}> = ({ label, tooltipText, children }) => {
    const { t } = useTranslation();
    return (
        <div className='custom-field-row'>
            <div className='custom-field-label'>
                {t(label)}
                {tooltipText && (
                    <span className="tooltip-icon">
                        <FontAwesomeIcon icon={faCircleQuestion} />
                        <span className="tooltip tooltip-right">{t(tooltipText)}</span>
                    </span>
                )}
            </div>
            <div className='custom-field-value'>
                {children}
            </div>
        </div>
    );
};

const ContractJobScheduleView: React.FC<{
    schedule?: ContractJobScheduleType;
    contractId: number;
    contractStartDate?: string | null;
    contract?: ContractType;
    forms: FormType[];
    workflows: ContractWorkflowOptionType[];
    onSaved: () => void;
}> = ({
    schedule,
    contractId,
    contractStartDate,
    contract,
    forms,
    workflows,
    onSaved,
}) => {
    const { t } = useTranslation();
    const { setFloatingAlert } = useGlobalContext();
    const { initializeModal } = useModal();
    const { userLocale } = useSettings();
    const { activeFeatures } = useAuthContext();
    const hasRightCheck = useAllowedRight;
    const editable = !hasRightCheck('only_view');

    // Fall back to the defaults of a new schedule when the contract has none yet
    const displaySchedule: Partial<ContractJobScheduleType> = schedule ?? {
        interval_type: 'yearly',
        generation_mode: 'lead_time',
        lead_time_days: 30,
        week_numbers: [],
        tasks: [],
    };

    // Hold the editing state and the edited values of the schedule
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [values, setValues] = useState<Record<string, any>>({});
    const [tasks, setTasks] = useState<ContractJobScheduleTaskType[]>([]);
    const [duration, setDuration] = useState<string>('');
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

    // Set one edited value of the schedule and clear its validation error
    const setValue = (name: string, value: any) => {
        setValues(previousValues => ({ ...previousValues, [name]: value }));
        setValidationErrors(previousErrors => {
            const { [name]: removedError, ...remainingErrors } = previousErrors;
            return remainingErrors;
        });
    };

    // Determine wheter the inspection list option is available
    const showInspectionOption = (
        activeFeatures.includes('forms_extended') &&
        !!contract?.has_asset_inspection_fields &&
        (!!contract?.all_assets || (contract?.assets || []).length > 0)
    );

    // Validate the required fields of the schedule
    const validate = () => {
        const errors: Record<string, string> = {};

        // A description is always required
        if (!values.description || !values.description.trim()) {
            errors.description = 'contract.job_schedule.description_required';
        }

        // A type is required when the job types module is active
        if (activeFeatures.includes('job_types') && !values.workflow) {
            errors.workflow = 'contract.job_schedule.workflow_required';
        }

        // The inspection task requires a description and a base checklist
        const inspectionTask = tasks.find(task => task.include_asset_inspection && !task.deleted);
        if (inspectionTask && !inspectionTask.description.trim()) {
            errors.inspection = 'contract.job_schedule.inspection_description_required';
        }
        if (inspectionTask && !inspectionTask.form) {
            errors.inspection = 'contract.job_schedule.inspection_form_required';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Clear the inspection error when the tasks change
    useEffect(() => {
        setValidationErrors(previousErrors => {
            const { inspection: removedError, ...remainingErrors } = previousErrors;
            return remainingErrors;
        });
    }, [tasks]);

    // Open the edit mode with the current values of the schedule
    const startEditing = () => {
        if (!editable || isEditing) return;
        setValues({
            workflow: displaySchedule.workflow ?? null,
            description: displaySchedule.description || '',
            interval_type: displaySchedule.interval_type,
            custom_interval: displaySchedule.custom_interval ? String(displaySchedule.custom_interval) : '',
            custom_unit: displaySchedule.custom_unit ?? null,
            week_numbers: (displaySchedule.week_numbers || []).map(String),
            first_execution_date: displaySchedule.first_execution_date ?? contractStartDate ?? null,
            generation_mode: displaySchedule.generation_mode,
            lead_time_days: String(displaySchedule.lead_time_days),
        });
        setTasks(displaySchedule.tasks || []);
        setDuration(displaySchedule.estimated_duration ? (formatAmount(Number(displaySchedule.estimated_duration), userLocale) || '') : '');
        setValidationErrors({});
        setIsEditing(true);
    };

    // Warn that saving adjusts the waiting jobs before really saving
    const confirmSave = () => {

        // Only save when the required fields are filled in
        if (!validate()) return;

        // Save directly when no jobs are waiting in the work storage
        if (!schedule || !schedule.waiting_jobs_count) {
            handleSave();
            return;
        }

        // Ask for a confirmation because the waiting jobs will be adjusted
        initializeModal(
            <WarningModal
                header='contract.job_schedule.save_warning_title'
                message='contract.job_schedule.save_warning_message'
                buttonLabel='general.save'
                onApproval={() => handleSave()}
            />,
            { modalSize: 'extra-small' }
        );
    };

    // Save the edited schedule with its tasks and duration
    const handleSave = async () => {
        setLoading(true);

        // Create the cancel token source
        const source = axios.CancelToken.source();

        try {
            // Save the schedule with all its edited values
            await saveData({
                apiObject: 'contract_job_schedule',
                itemId: schedule?.id,
                data: {
                    ...values,
                    custom_interval: values.custom_interval ? Number(values.custom_interval) : null,
                    contract: contractId,
                    tasks,
                    estimated_duration: parseDurationFromAmount(duration),
                },
                source,
            });

            // Show a success alert and refresh the schedule
            setFloatingAlert({ 'type': 'success' });
            setIsEditing(false);
            onSaved();
        } catch (error) {
            console.error('Saving the contract schedule failed', error);
        } finally {
            setLoading(false);
        }
    };

    // Close the edit mode without saving
    const handleCancel = () => {
        setValidationErrors({});
        setIsEditing(false);
    };

    // Format a date value to the user locale
    const formatDate = (value: string | null) => {
        if (!value) return '-';
        return new Date(value).toLocaleDateString(userLocale, { year: 'numeric', month: '2-digit', day: '2-digit' });
    };

    // Build the repetition text of the schedule
    const repetitionText = () => {
        if (!schedule) return '-';
        if (displaySchedule.interval_type === 'custom' && displaySchedule.custom_interval && displaySchedule.custom_unit) {
            return `${t('contract.job_schedule.custom_interval_label')} ${displaySchedule.custom_interval} ${t(`contract.custom_unit.${displaySchedule.custom_unit}`).toLowerCase()}`;
        }
        if (displaySchedule.interval_type === 'week_numbers') {
            const weekNumbers = (displaySchedule.week_numbers || []).join(', ');
            return `${t('contract.interval.week_numbers').toLowerCase()} ${weekNumbers}`;
        }
        return t(`contract.interval.${displaySchedule.interval_type}`);
    };

    // Build the lead time text of the schedule
    const leadTimeText = () => {
        const leadTimeOption = LEAD_TIME_OPTIONS.find(option => option.value === String(displaySchedule.lead_time_days));
        if (leadTimeOption) return t(leadTimeOption.name);
        return t('contract.job_schedule.days_value', { days: displaySchedule.lead_time_days });
    };

    // Get the visible tasks for the view mode
    const visibleTasks = (displaySchedule.tasks || []).filter(task => !task.deleted);

    // Get the name of a linked form of a task
    const getFormName = (task: ContractJobScheduleTaskType) => {
        const form = forms.find(form => form.id === task.form);
        return form ? form.name : null;
    };

    // Get the workflow of the saved schedule to show its default tasks in the view mode
    const scheduleWorkflow = workflows.find(workflow => workflow.id === displaySchedule.workflow) || null;

    // Get the visible default tasks of the schedule workflow for the view mode
    const visibleDefaultTasks = (scheduleWorkflow?.default_tasks || []).filter(task => !task.deleted);

    // Get the name of a linked form of a default task
    const getDefaultTaskFormName = (task: JobWorkflowDefaultTaskType) => {
        const form = forms.find(form => form.id === task.form);
        return form ? form.name : null;
    };

    // Get the lead time options which fit the chosen interval
    const leadTimeOptions = values.interval_type === 'weekly' ? LEAD_TIME_OPTIONS.slice(0, 2) : LEAD_TIME_OPTIONS;

    // Get the selected workflow to show its default tasks while editing
    const selectedWorkflow = workflows.find(workflow => workflow.id === values.workflow) || null;

    return (
        <div className={`card custom-fields-live-edit multi contract-schedule ${editable ? 'editable' : ''} ${isEditing ? 'editing' : ''}`}
             onClick={startEditing}>
            <div className='custom-field-list'>
                <div className='custom-field-items'>

                    {/* The work which has to be performed */}
                    <div className='custom-field-section'>
                        <div className='custom-field-section-header'>
                            <h5>{t('contract.job_schedule.work_details_card_label')}</h5>
                        </div>

                        {activeFeatures.includes('job_types') && (
                            <ScheduleRow label='contract.job_schedule.workflow_label'>
                                {isEditing ? (
                                    <>
                                        <Dropdown<ContractWorkflowOptionType>
                                            options={workflows}
                                            name='workflow'
                                            disabled_selected={t('job.general.type_disabled_selected')}
                                            selectedOption={workflows.find(workflow => workflow.id === values.workflow) || null}
                                            onChange={(selectedValue) => setValue('workflow', selectedValue === 'none' ? null : Number(selectedValue))}
                                            selectionFormat={(option) => `${option.name}`}
                                            optionFormat='name'
                                            showSearch={false}
                                            allowNoneOption={false}
                                            isInvalid={!!validationErrors.workflow}
                                        />
                                        {validationErrors.workflow && (
                                            <div className='error-message'>
                                                {t(validationErrors.workflow)}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    displaySchedule.workflow_name || '-'
                                )}
                            </ScheduleRow>
                        )}

                        <ScheduleRow label='contract.job_schedule.description_label'>
                            {isEditing ? (
                                <>
                                    <textarea
                                        name='description'
                                        rows={3}
                                        value={values.description}
                                        placeholder={t('contract.job_schedule.description_placeholder')}
                                        onChange={(event) => setValue('description', event.target.value)}
                                        style={{ height: 'auto', minHeight: '72px' }}
                                        className={validationErrors.description ? 'is-invalid' : ''}
                                    />
                                    {validationErrors.description && (
                                        <div className='error-message'>
                                            {t(validationErrors.description)}
                                        </div>
                                    )}
                                </>
                            ) : (
                                displaySchedule.description || '-'
                            )}
                        </ScheduleRow>

                        <ScheduleRow label='contract.job_schedule.tasks_label'>
                            {isEditing ? (
                                <>
                                    {selectedWorkflow?.default_tasks && selectedWorkflow.default_tasks.length > 0 && (
                                        <ContractScheduleDefaultTaskRows
                                            tasks={selectedWorkflow.default_tasks}
                                            forms={forms}
                                        />
                                    )}
                                    <ContractScheduleTaskRows
                                        tasks={tasks}
                                        setTasks={setTasks}
                                        forms={forms}
                                        showInspectionOption={showInspectionOption}
                                        inspectionError={validationErrors.inspection}
                                    />
                                </>
                            ) : (
                                (visibleDefaultTasks.length > 0 || visibleTasks.length > 0) ? (
                                    <>
                                    {visibleDefaultTasks.map((task, index) => (
                                        <div key={`default-${task.id ?? index}`} className='contract-schedule-task-line'>
                                            {task.description || getDefaultTaskFormName(task)}
                                            {task.required && (
                                                <span className='task-required-icon active tooltip-icon'>
                                                    <FontAwesomeIcon icon={faLock} />
                                                    <span className="tooltip">{t('job.tasks.task_required_readonly_tooltip')}</span>
                                                </span>
                                            )}
                                            {task.description && getDefaultTaskFormName(task) && (
                                                <span className='contract-schedule-task-form'>
                                                    {' ('}
                                                    {getDefaultTaskFormName(task)}
                                                    {')'}
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                    {visibleTasks.map((task, index) => (
                                        <div key={task.id ?? index} className='contract-schedule-task-line'>
                                            {task.description}
                                            {task.required && (
                                                <span className='task-required-icon active tooltip-icon'>
                                                    <FontAwesomeIcon icon={faLock} />
                                                    <span className="tooltip">{t('job.tasks.task_required_readonly_tooltip')}</span>
                                                </span>
                                            )}
                                            {(getFormName(task) || task.include_asset_inspection) && (
                                                <span className='contract-schedule-task-form'>
                                                    {' ('}
                                                    {getFormName(task)}
                                                    {task.include_asset_inspection && ` - ${t('contract.job_schedule.include_asset_inspection_view_label')}`}
                                                    {')'}
                                                </span>
                                            )}
                                            {task.include_asset_inspection && (
                                                <span className="tooltip-icon">
                                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                                    <span className="tooltip tooltip-right">
                                                        {t('contract.job_schedule.include_asset_inspection_view_tooltip')}
                                                    </span>
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                    </>
                                ) : '-'
                            )}
                        </ScheduleRow>

                        <ScheduleRow label='contract.job_schedule.estimated_duration_label'>
                            {isEditing ? (
                                <TextInput
                                    name='estimated_duration'
                                    placeholder='job.general.estimated_duration_placeholder'
                                    value={duration}
                                    onChange={(value) => setDuration(value)}
                                />
                            ) : (
                                displaySchedule.estimated_duration ? (formatAmount(Number(displaySchedule.estimated_duration), userLocale) || '-') : '-'
                            )}
                        </ScheduleRow>
                    </div>

                    {/* The rhythm the work repeats in */}
                    <div className='custom-field-section'>
                        <div className='custom-field-section-header'>
                            <h5>{t('contract.job_schedule.repetition_card_label')}</h5>
                        </div>

                        <ScheduleRow label='contract.job_schedule.interval_type_label'>
                            {isEditing ? (
                                <Dropdown
                                    options={INTERVAL_OPTIONS}
                                    name='interval_type'
                                    selectedOption={INTERVAL_OPTIONS.find(option => option.value === values.interval_type) || null}
                                    onChange={(selectedValue) => setValue('interval_type', selectedValue)}
                                    selectionFormat={(option) => t(option.name)}
                                    optionFormat={(option) => t(option.name)}
                                    backendField='value'
                                    showSearch={false}
                                    allowNoneOption={false}
                                />
                            ) : (
                                repetitionText()
                            )}
                        </ScheduleRow>

                        {isEditing && values.interval_type === 'custom' && (
                            <ScheduleRow label='contract.job_schedule.custom_interval_label'>
                                <div className='contract-schedule-custom-interval'>
                                    <TextInput
                                        name='custom_interval'
                                        placeholder='contract.job_schedule.custom_interval_placeholder'
                                        value={values.custom_interval}
                                        onChange={(value) => setValue('custom_interval', value.replace(/[^0-9]/g, ''))}
                                    />
                                    <Dropdown
                                        options={CUSTOM_UNIT_OPTIONS}
                                        name='custom_unit'
                                        disabled_selected={t('contract.job_schedule.custom_unit_label')}
                                        selectedOption={CUSTOM_UNIT_OPTIONS.find(option => option.value === values.custom_unit) || null}
                                        onChange={(selectedValue) => setValue('custom_unit', selectedValue)}
                                        selectionFormat={(option) => t(option.name)}
                                        optionFormat={(option) => t(option.name)}
                                        backendField='value'
                                        showSearch={false}
                                        allowNoneOption={false}
                                    />
                                </div>
                            </ScheduleRow>
                        )}

                        {isEditing && values.interval_type === 'week_numbers' && (
                            <ScheduleRow label='contract.job_schedule.week_numbers_label'>
                                <MultiSelect
                                    options={WEEK_NUMBER_OPTIONS}
                                    selectedOption={WEEK_NUMBER_OPTIONS.filter(option => (values.week_numbers || []).includes(option.value))}
                                    onChange={(selectedValue) => {
                                        const currentWeekNumbers: string[] = values.week_numbers || [];
                                        const updatedWeekNumbers = currentWeekNumbers.includes(selectedValue)
                                            ? currentWeekNumbers.filter(weekNumber => weekNumber !== selectedValue)
                                            : [...currentWeekNumbers, selectedValue];
                                        setValue('week_numbers', updatedWeekNumbers);
                                    }}
                                    disabled_selected={t('contract.job_schedule.week_numbers_disabled_selected')}
                                    sortOptions={false}
                                    allowNoneOption={false}
                                />
                            </ScheduleRow>
                        )}

                        <ScheduleRow label='contract.job_schedule.first_execution_date_label'
                                     tooltipText='contract.job_schedule.first_execution_date_helper'>
                            {isEditing ? (
                                <DateInput
                                    name='first_execution_date'
                                    value={values.first_execution_date ? new Date(values.first_execution_date) : null}
                                    onChange={(value) => setValue('first_execution_date', value)}
                                />
                            ) : (
                                formatDate(displaySchedule.first_execution_date ?? null)
                            )}
                        </ScheduleRow>

                        <ScheduleRow label='contract.job_schedule.generation_mode_label'
                                     tooltipText='contract.job_schedule.generation_mode_tooltip'>
                            {isEditing ? (
                                <Dropdown
                                    options={GENERATION_MODE_OPTIONS}
                                    name='generation_mode'
                                    selectedOption={GENERATION_MODE_OPTIONS.find(option => option.value === values.generation_mode) || null}
                                    onChange={(selectedValue) => setValue('generation_mode', selectedValue)}
                                    selectionFormat={(option) => t(option.name)}
                                    optionFormat={(option) => t(option.name)}
                                    backendField='value'
                                    showSearch={false}
                                    allowNoneOption={false}
                                />
                            ) : (
                                schedule ? t(`contract.generation_mode.${displaySchedule.generation_mode}`) : '-'
                            )}
                        </ScheduleRow>

                        {(isEditing ? values.generation_mode === 'lead_time' : !!schedule && displaySchedule.generation_mode === 'lead_time') && (
                            <ScheduleRow label='contract.job_schedule.lead_time_days_label'>
                                {isEditing ? (
                                    <Dropdown
                                        options={leadTimeOptions}
                                        name='lead_time_days'
                                        selectedOption={leadTimeOptions.find(option => option.value === values.lead_time_days) || null}
                                        onChange={(selectedValue) => setValue('lead_time_days', selectedValue)}
                                        selectionFormat={(option) => t(option.name)}
                                        optionFormat={(option) => t(option.name)}
                                        backendField='value'
                                        showSearch={false}
                                        allowNoneOption={false}
                                    />
                                ) : (
                                    leadTimeText()
                                )}
                            </ScheduleRow>
                        )}
                    </div>
                </div>
                {editable && !isEditing && (
                    <span className='edit-icon'>
                        <FontAwesomeIcon icon={faPen} />
                    </span>
                )}
            </div>
            {isEditing && (
                <div className='button-row'>
                    <div className='buttons-right'>
                        <SecondaryButton
                            label='general.cancel'
                            size='extra-small'
                            onClick={handleCancel}
                        />
                        <PrimaryButton
                            label='general.save'
                            size='extra-small'
                            loading={loading}
                            onClick={confirmSave}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContractJobScheduleView;
